import { Injectable, inject, signal } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { environment } from '../../../environments/environment';

/**
 * OPS-03-T01: an installed app keeps running the build it started with. The browser downloads the next release in the
 * background; this service learns that it is ready and the header offers one tap to move to it (never a silent reload — the user
 * may be in the middle of a form).
 */
@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  private readonly swUpdate = inject(SwUpdate, { optional: true });
  /** A newer build is downloaded and only waits for `reload()`. */
  readonly updateReady = signal(false);
  private readonly RECOVERY_KEY = 'pwa.recoveryReload';
  private watched = false;

  /** Idempotent: the first caller starts the watch, everyone after it reads the same signal. */
  start(): void {
    if (this.watched || !environment.serviceWorker || !this.swUpdate) return;
    this.watched = true;
    this.swUpdate.versionUpdates.subscribe((event) => {
      if (event.type === 'VERSION_READY') this.updateReady.set(true);
      // A half-installed build cannot be repaired from inside the page: the recovery is a reload, at most once.
      if (event.type === 'VERSION_INSTALLATION_FAILED') this.recoverOnce();
    });
    this.swUpdate.unrecoverable.subscribe(() => this.recoverOnce());
    // The app ran fine after a recovery, so the next failed install may recover again.
    setTimeout(() => this.clearRecovery(), 30_000);
  }

  /** Moves to the downloaded build. Failing to activate must not trap the user: the reload still fetches the new shell. */
  async reload(): Promise<void> {
    try {
      await this.swUpdate?.activateUpdate();
    } catch {
      /* the reload below is what matters */
    }
    location.reload();
  }

  /** One reload per session for a broken install — a build that fails every time must not reload forever. */
  private recoverOnce(): void {
    let tried = false;
    try {
      tried = sessionStorage.getItem(this.RECOVERY_KEY) === '1';
      sessionStorage.setItem(this.RECOVERY_KEY, '1');
    } catch {
      /* storage blocked: one reload, then the browser's own retry */
    }
    if (!tried) location.reload();
  }

  private clearRecovery(): void {
    try {
      sessionStorage.removeItem(this.RECOVERY_KEY);
    } catch {
      /* storage blocked */
    }
  }
}
